// src/components/complaint/ComplaintDetail.tsx
import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import {
  ArrowLeft, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare,
  Upload, Download, RefreshCw, FileText, Image as ImageIcon,
  ChevronRight, Send, Paperclip, MoreHorizontal, User, Shield, X
} from 'lucide-react';
import { toast } from 'sonner';
import { Seo } from "../../components/Seo";
import { getComplaintDetail, addComment, uploadEvidence, reopenComplaint, escalateComplaint, downloadEvidence, downloadAllEvidence } from '../../../services/complaint.service';

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function ComplaintDetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-6 w-28 rounded-full" />
          </div>
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="w-10 h-10 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <Skeleton className="h-7 w-72 mb-4" />
            {[1,2,3].map(i => <Skeleton key={i} className="h-4 w-full mb-2" />)}
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <div className="flex justify-between mb-5"><Skeleton className="h-6 w-40" /><Skeleton className="h-5 w-24" /></div>
            {[1,2,3].map(item => (
              <div key={item} className="flex gap-3 items-center mb-3">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <div className="flex-1"><Skeleton className="h-4 w-44 mb-2" /><Skeleton className="h-3 w-16" /></div>
                <Skeleton className="w-8 h-8 rounded-lg" />
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <Skeleton className="h-6 w-44 mb-6" />
            {[1,2,3].map(item => (
              <div key={item} className="flex gap-4 mb-6">
                <Skeleton className="w-8 h-8 rounded-full" />
                <div className="flex-1 bg-[#F5F7FA] rounded-xl p-4">
                  <div className="flex justify-between mb-2"><Skeleton className="h-4 w-36" /><Skeleton className="h-5 w-16 rounded-full" /></div>
                  <Skeleton className="h-3 w-32 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <Skeleton className="h-6 w-36 mb-4" />
            <Skeleton className="h-24 w-full rounded-xl mb-4" />
            <div className="flex justify-between"><Skeleton className="h-5 w-28" /><Skeleton className="h-10 w-28 rounded-xl" /></div>
          </div>
        </div>
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <Skeleton className="h-5 w-32 mb-4" />
            <div className="flex gap-3"><Skeleton className="w-16 h-12 rounded-lg" /><div><Skeleton className="h-4 w-32 mb-2" /><Skeleton className="h-3 w-20 mb-2" /><Skeleton className="h-4 w-24" /></div></div>
            <Skeleton className="h-4 w-24 mt-4" />
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <Skeleton className="h-5 w-32 mb-4" />
            <div className="flex gap-3"><Skeleton className="w-10 h-10 rounded-full" /><div><Skeleton className="h-4 w-28 mb-2" /><Skeleton className="h-3 w-24" /></div></div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <Skeleton className="h-5 w-20 mb-4" />
            {[1,2,3,4].map(btn => <Skeleton key={btn} className="h-11 w-full rounded-xl mb-3" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ComplaintDetail() {
  const { t } = useTranslation('auth'); 
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [complaint, setComplaint] = useState<any>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [uploading, setUploading] = useState(false);

  // Status config with translations
  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
      submitted: { label: t('status.submitted'), color: 'text-[#1E6DFF]', bg: 'bg-[#1E6DFF]/10', icon: <Clock className="w-3.5 h-3.5" /> },
      under_review: { label: t('status.under_review'), color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10', icon: <AlertCircle className="w-3.5 h-3.5" /> },
      awaiting_response: { label: t('status.awaiting_response'), color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/10', icon: <MessageSquare className="w-3.5 h-3.5" /> },
      resolved: { label: t('status.resolved'), color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10', icon: <CheckCircle className="w-3.5 h-3.5" /> },
      rejected: { label: t('status.rejected'), color: 'text-[#EF4444]', bg: 'bg-[#EF4444]/10', icon: <XCircle className="w-3.5 h-3.5" /> },
      closed: { label: t('status.closed'), color: 'text-[#717182]', bg: 'bg-[#717182]/10', icon: <XCircle className="w-3.5 h-3.5" /> },
    };
    return configs[status] || configs.submitted;
  };

  const ROLE_STYLES: Record<string, string> = {
    buyer: 'bg-[#1E6DFF]/10 text-[#1E6DFF]',
    admin: 'bg-[#8B5CF6]/10 text-[#8B5CF6]',
    system: 'bg-[#717182]/10 text-[#717182]',
    seller: 'bg-[#F59E0B]/10 text-[#F59E0B]',
  };

  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await getComplaintDetail(id);
      if (res.success) {
        setComplaint(res.data);
      } else {
        toast.error(res.message || t('messages.load_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('messages.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const sendComment = async () => {
    if (!comment.trim() || !id) return;
    try {
      setSending(true);
      const res = await addComment(id, comment);
      if (res.success) {
        toast.success(t('messages.comment_added'));
        setComment('');
        await fetchDetail();
      } else {
        toast.error(res.message || t('messages.comment_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('messages.comment_failed'));
    } finally {
      setSending(false);
    }
  };
  const handleDownloadAllEvidence = async () => {
  if (!complaint?.id) return;

  try {
    // Get ZIP file from API
    const blob = await downloadAllEvidence(complaint.id);

    // Create temporary URL
    const url = window.URL.createObjectURL(
      new Blob([blob], {
        type: 'application/zip',
      })
    );

    // Create download link
    const link = document.createElement('a');

    link.href = url;

    link.setAttribute(
      'download',
      `${complaint.referenceNumber}-evidence.zip`
    );

    document.body.appendChild(link);

    link.click();

    // Cleanup
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(
      t('messages.download_started') ||
      'Download started'
    );

  } catch (err: any) {
    console.error(
      'Download all evidence error:',
      err
    );

    toast.error(
      err?.message ||
      'Failed to download evidence files'
    );
  }
};

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !id) return;
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('evidence', f));
    try {
      setUploading(true);
      const res = await uploadEvidence(id, formData);
      if (res.success) {
        toast.success(t('messages.evidence_uploaded'));
        await fetchDetail();
      } else {
        toast.error(res.message || t('messages.evidence_upload_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('messages.evidence_upload_failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    try {
      const res = await reopenComplaint(id, reopenReason);
      if (res.success) {
        toast.success(t('messages.reopened'));
        setShowReopenModal(false);
        setReopenReason('');
        await fetchDetail();
      } else {
        toast.error(res.message || t('messages.reopen_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('messages.reopen_failed'));
    }
  };

  const handleEscalate = async () => {
    if (!id) return;
    try {
      const res = await escalateComplaint(id);
      if (res.success) {
        toast.success(t('messages.escalated'));
        await fetchDetail();
      } else {
        toast.error(res.message || t('messages.escalate_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('messages.escalate_failed'));
    }
  };

    const handleDownloadEvidence = async (complaintId: string, evidenceId: string, fileName: string) => {
    try {
      // 1. API se file blob mangwayein
      const blob = await downloadEvidence(complaintId, evidenceId);
      
      // 2. Blob ka temporary URL banayein
      const url = window.URL.createObjectURL(new Blob([blob]));
      
      // 3. Ek temporary anchor tag banakar click trigger karein
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName); 
      document.body.appendChild(link);
      link.click();
      
      // 4. Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('messages.download_started') || 'Download started');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download file');
    }
  };
 

  if (loading) return <ComplaintDetailSkeleton />;
  if (!complaint) return <div className="text-center py-10">{t('messages.not_found')}</div>;

  const status = getStatusConfig(complaint.status);

  return (
     <>
    <Seo
  title={
    complaint.subject
      ? t('detail.seo.titleWithSubject', { subject: complaint.subject })
      : t('detail.seo.titleWithoutSubject')
  }
  description={
    complaint.description ||
    t('detail.seo.descriptionFallback', { referenceNumber: complaint.referenceNumber })
  }
/>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/complaints')} className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl text-[#2D323A]">{t('detail.title')} #{complaint.referenceNumber}</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs ${status.bg} ${status.color}`}>
              {status.icon} {status.label}
            </span>
          </div>
          <p className="text-sm text-[#717182] mt-0.5">{complaint.category} · {t('detail.filed')} {new Date(complaint.createdAt).toLocaleDateString()}</p>
        </div>
        <div className="flex gap-2">
          <button className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors" title={t('detail.more_options')}>
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Subject & Description */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <h2 className="text-[#2D323A] mb-1">{complaint.subject}</h2>
            <p className="text-sm text-[#717182] leading-relaxed">{complaint.description}</p>
          </div>

          {/* Evidence */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#2D323A]">{t('detail.evidence')}</h2>
              <label className="flex items-center gap-1.5 text-sm text-[#1E6DFF] hover:underline cursor-pointer">
                <Upload className="w-4 h-4" /> {t('detail.add_more')}
                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFileUpload} />
              </label>
            </div>
            {complaint.evidence?.length > 0 ? (
              <div className="space-y-2">
                {complaint.evidence.map((e: any) => (
                  <div key={e.id} className="flex items-center gap-3 p-3 bg-[#F5F7FA] rounded-xl border border-[#E5E7EB]">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${e.mimeType === 'application/pdf' ? 'bg-[#EF4444]/10' : 'bg-[#1E6DFF]/10'}`}>
                      {e.mimeType === 'application/pdf' ? <FileText className="w-5 h-5 text-[#EF4444]" /> : <ImageIcon className="w-5 h-5 text-[#1E6DFF]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#2D323A] truncate">{e.fileName}</p>
                      <p className="text-xs text-[#717182]">{(e.size / 1024).toFixed(0)} KB</p>
                    </div>
                    {/* <a href={e.filePath} download className="p-1.5 hover:bg-white rounded-lg text-[#717182] hover:text-[#1E6DFF] transition-colors">
                      <Download className="w-4 h-4" />
                    </a> */}
                    <button 
  onClick={() => handleDownloadEvidence(complaint.id, e.id, e.fileName)}
  className="p-1.5 hover:bg-white rounded-lg text-[#717182] hover:text-[#1E6DFF] transition-colors"
  title="Download Evidence"
>
  <Download className="w-4 h-4" />
</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#717182]">{t('detail.no_evidence')}</p>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <h2 className="text-[#2D323A] mb-5">{t('detail.timeline')}</h2>
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E5E7EB]" />
              <div className="space-y-6">
                {/* {complaint.timeline?.map((t: any, i: number) => (
                  <div key={i} className="relative pl-10">
                    <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center text-xs ${ROLE_STYLES[t.role]}`}>
                      {t.role === 'buyer' ? <User className="w-4 h-4" /> : t.role === 'admin' ? <Shield className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div className="bg-[#F5F7FA] rounded-xl p-3.5 border border-[#E5E7EB]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-[#2D323A]">{t.event}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_STYLES[t.role]}`}>{t.actor}</span>
                      </div>
                      <p className="text-xs text-[#717182] mb-1">{new Date(t.createdAt).toLocaleString()}</p>
                      {t.note && <p className="text-sm text-[#717182]">{t.note}</p>}
                    </div>
                  </div>
                ))} */}
                {complaint.timeline?.map((item: any, i: number) => (
  <div key={i} className="relative pl-10">
    <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center text-xs ${ROLE_STYLES[item.role]}`}>
      {item.role === 'buyer' ? <User className="w-4 h-4" /> : item.role === 'admin' ? <Shield className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
    </div>
    <div className="bg-[#F5F7FA] rounded-xl p-3.5 border border-[#E5E7EB]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-[#2D323A]">{item.event}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_STYLES[item.role]}`}>{item.actor}</span>
      </div>
      <p className="text-xs text-[#717182] mb-1">{new Date(item.createdAt).toLocaleString()}</p>
      
      {/* Yahan check lagayein ki note mein encrypted key na ho */}
      {item.note && !item.note.includes('x/asEmSfZnR') && (
        <p className="text-sm text-[#717182]">{item.note}</p>
      )}
    </div>
  </div>
))}
              </div>
            </div>
          </div>

          {/* Add Comment */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <h2 className="text-[#2D323A] mb-4">{t('detail.add_comment')}</h2>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              placeholder={t('detail.comment_placeholder')}
              className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors resize-none text-sm mb-3"
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[#717182] hover:text-[#1E6DFF] transition-colors cursor-pointer">
                <Paperclip className="w-4 h-4" /> {t('detail.attach_file')}
                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFileUpload} />
              </label>
              <button
                onClick={sendComment}
                disabled={!comment.trim() || sending}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-sm disabled:opacity-50"
              >
                {sending ? t('detail.sending') : <><Send className="w-4 h-4" /> {t('detail.send')}</>}
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-5">
          {/* Related Vehicle */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <h3 className="text-sm text-[#717182] mb-3">{t('detail.related_vehicle')}</h3>
            {complaint.vehicle ? (
              <div className="flex items-start gap-3">
                <div className="w-16 h-12 bg-[#F5F7FA] rounded-lg flex items-center justify-center flex-shrink-0 border border-[#E5E7EB]">
                  {complaint.vehicle.image ? (
                    <img src={complaint.vehicle.image} alt={complaint.vehicle.title} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <span className="text-2xl">🚗</span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#2D323A]">{complaint.vehicle.title}</p>
                  <p className="text-sm text-[#1E6DFF]">{complaint.vehicle.price} MAD</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#717182]">{t('detail.no_vehicle')}</p>
            )}
          </div>

          {/* Seller */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <h3 className="text-sm text-[#717182] mb-3">{t('detail.seller')}</h3>
            <p className="text-sm text-[#2D323A]">{complaint.sellerName || t('detail.not_specified')}</p>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-2">
            <h3 className="text-sm text-[#717182] mb-3">{t('detail.actions')}</h3>
            <button
              onClick={() => document.querySelector('input[type="file"]')?.click()}
              className="w-full flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:border-[#1E6DFF] hover:text-[#1E6DFF] transition-colors text-sm"
            >
              <Upload className="w-4 h-4" /> {t('detail.upload_evidence')}
            </button>
            <button onClick={handleDownloadAllEvidence} disabled={!complaint.evidence?.length} className="w-full flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:border-[#1E6DFF] hover:text-[#1E6DFF] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <Download className="w-4 h-4" />{t('detail.download_all')}
            </button>
            {['resolved', 'closed', 'rejected'].includes(complaint.status) && (
              <button
                onClick={() => setShowReopenModal(true)}
                className="w-full flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors text-sm"
              >
                <RefreshCw className="w-4 h-4" /> {t('detail.reopen')}
              </button>
            )}
            {!['resolved', 'closed', 'rejected'].includes(complaint.status) && (
              <button
                onClick={handleEscalate}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-xl hover:bg-[#8B5CF6]/20 transition-colors text-sm"
              >
                <Shield className="w-4 h-4" /> {t('detail.escalate')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reopen Modal */}
      {showReopenModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#2D323A]">{t('detail.reopen_modal.title')}</h3>
              <button onClick={() => { setShowReopenModal(false); setReopenReason(''); }}><X className="w-5 h-5 text-[#717182]" /></button>
            </div>
            <p className="text-sm text-[#717182] mb-5">{t('detail.reopen_modal.description')}</p>
            <textarea
              rows={3}
              placeholder={t('detail.reopen_modal.reason_placeholder')}
              value={reopenReason}
              onChange={e => setReopenReason(e.target.value)}
              className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm mb-4 resize-none"
            />
            <div className="flex gap-3">
              <button onClick={() => { setShowReopenModal(false); setReopenReason(''); }} className="flex-1 py-2.5 border border-[#E5E7EB] rounded-xl text-[#2D323A] text-sm hover:border-[#1E6DFF] transition-colors">
                {t('detail.reopen_modal.cancel')}
              </button>
              <button onClick={handleReopen} className="flex-1 py-2.5 bg-[#F59E0B] text-white rounded-xl text-sm hover:bg-[#d97706] transition-colors">
                {t('detail.reopen_modal.reopen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
     </>
  );
 
}